import { inject, Injectable } from '@angular/core';
import { DURATION_TOKENS, getLocale } from '@lx/shared/i18n';
import { CourseDuration, DurationUnit, DurationUnitEnum } from '@lx/shared/data-access/types';
import { isNumeric } from '../functions';

@Injectable({
  providedIn: 'root',
})
export class DurationUnitsService {
  private readonly durationUnitMap: Record<'singular' | 'plural', Record<DurationUnit, string>> = inject(DURATION_TOKENS);
  private readonly locale = getLocale();
  private readonly formatter = new Intl.NumberFormat(this.locale, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 10,
  });

  getPerDurationText(quantity: number | string, durationUnit: DurationUnit = 'WEEKS'): string {
    if (quantity !== 1) {
      return this.getDurationText(quantity, durationUnit);
    }

    switch (durationUnit) {
      case DurationUnitEnum.EACH:
      case DurationUnitEnum.LESSONS:
        return $localize`per lesson`;
      case DurationUnitEnum.HOURS:
        return $localize`per hour`;
      case DurationUnitEnum.DAYS:
        return $localize`per day`;
      case DurationUnitEnum.MONTH:
        return $localize`per month`;
      case DurationUnitEnum.YEARS:
        return $localize`per year`;
      case DurationUnitEnum.WEEKS:
      default:
        return $localize`per week`;
    }
  }

  getDurationText(quantity: number | string | undefined | null, durationUnit: DurationUnit = 'WEEKS'): string {
    if (!quantity && quantity !== 0) {
      return this.durationUnitMap.singular[durationUnit] ?? '';
    }

    // Convert to number and format the quantity for display
    const numericQuantity = Number(quantity);
    const formattedQuantity = this.formatDuration(numericQuantity);

    switch (durationUnit) {
      case DurationUnitEnum.EACH:
      case DurationUnitEnum.LESSONS:
        return numericQuantity === 1 ? $localize`1 lesson` : $localize`${formattedQuantity} lessons`;
      case DurationUnitEnum.HOURS:
        return numericQuantity === 1 ? $localize`1 hour` : $localize`${formattedQuantity} hours`;
      case DurationUnitEnum.DAYS:
        return numericQuantity === 1 ? $localize`1 day` : $localize`${formattedQuantity} days`;
      case DurationUnitEnum.MONTH:
        return numericQuantity === 1 ? $localize`1 month` : $localize`${formattedQuantity} months`;
      case DurationUnitEnum.YEARS:
        return numericQuantity === 1 ? $localize`1 year` : $localize`${formattedQuantity} years`;
      case DurationUnitEnum.WEEKS:
      default:
        return numericQuantity === 1 ? $localize`1 week` : $localize`${formattedQuantity}:number_of_weeks: weeks`;
    }
  }

  /**
   * Returns duration text in short format (e.g., "1 h" instead of "1 hour")
   * @param duration - The quantity of the duration unit
   * @param durationUnit - The duration unit (HOURS, DAYS, WEEKS, etc.)
   * @returns Formatted short duration text
   * @example
   * // Returns "1 h"
   * getShortDurationText(1, 'HOURS');
   *
   * // Returns "5 d"
   * getShortDurationText(5, 'DAYS');
   */
  getShortDurationText(duration: number | string | undefined | null, durationUnit: DurationUnit = 'WEEKS'): string {
    if (duration === null || duration === undefined) {
      return '';
    }

    const durationText = this.getDurationText(duration, durationUnit);

    const durationAndUnit = durationText.split(' ');
    return `${durationAndUnit[0]} ${durationAndUnit[1][0]}`;
  }

  getDurationRange(duration: CourseDuration): string {
    if (!duration) {
      return '';
    }
    const { durationMin, durationMax, durationUnit } = duration;
    if (durationMin === durationMax) {
      return this.getDurationText(durationMin, durationUnit);
    }

    const formattedDurationMin = this.formatDuration(durationMin);
    const formattedDurationMax = this.formatDuration(durationMax);

    switch (durationUnit) {
      case DurationUnitEnum.EACH:
      case DurationUnitEnum.LESSONS:
        return $localize`${formattedDurationMin}:duration_min: - ${formattedDurationMax}:duration_max: lessons`;
      case DurationUnitEnum.HOURS:
        return $localize`${formattedDurationMin}:duration_min: - ${formattedDurationMax}:duration_max: hours`;
      case DurationUnitEnum.DAYS:
        return $localize`${formattedDurationMin}:duration_min: - ${formattedDurationMax}:duration_max: days`;
      case DurationUnitEnum.MONTH:
        return $localize`${formattedDurationMin}:duration_min: - ${formattedDurationMax}:duration_max: months`;
      case DurationUnitEnum.YEARS:
        return $localize`${formattedDurationMin}:duration_min: - ${formattedDurationMax}:duration_max: years`;
      case DurationUnitEnum.WEEKS:
      default:
        return $localize`${formattedDurationMin}:duration_min: - ${formattedDurationMax}:duration_max: weeks`;
    }
  }

  /**
   * Returns the appropriate singular or plural form of a duration unit based on the provided quantity. If the quantity is null or undefined,
   * it defaults to the singular form. If the quantity is zero or one, it also returns the singular form.
   * For all other numeric quantities, it returns the plural form.
   * If the quantity is a non-numeric string, it returns that string as is.
   * @param durationUnit the duration unit for which the singular or plural form is needed
   * @param quantity the quantity to determine singular or plural form (it could be a number, string, null, or undefined)
   * @returns the appropriate singular or plural form of the duration unit, or the non-numeric string if provided
   */
  getDurationUnit(durationUnit: DurationUnit, quantity?: number | string | undefined | null): string {
    // Handle null/undefined - return singular form
    if (quantity === null || quantity === undefined) {
      return this.durationUnitMap.singular[durationUnit] ?? '';
    }

    // Handle non-numeric strings - this seems like edge case behavior
    // Consider if this is actually needed or a bug
    if (!isNumeric(quantity)) {
      return quantity as string;
    }

    // Convert to number for comparison
    const numericQuantity = Number(quantity);

    // Handle zero and one - return singular form
    if (numericQuantity === 0 || numericQuantity === 1) {
      return this.durationUnitMap.singular[durationUnit] ?? '';
    }

    // All other cases - return plural form
    return this.durationUnitMap.plural[durationUnit] ?? '';
  }

  /**
   * Formats a duration value to a string. If the value is null, undefined, or not finite, it returns an empty string. If the value is a whole number,
   * it returns the number as a string. Otherwise, it formats the number using the Intl.NumberFormat API.
   * The main idea is to ensure that the duration is presented in a user-friendly format, especially for non-integer values and using the appropriate locale settings.
   * @param value
   * @private
   */
  private formatDuration(value: number | null | undefined): string {
    if (value === null || value === undefined) {
      return '';
    }

    if (!Number.isFinite(value)) {
      return '';
    }

    // Check if the number is a whole number (no decimals)
    if (Number.isInteger(value)) {
      return value.toString();
    }

    return this.formatter.format(value);
  }
}
